// @wc-ignore-file
// CR-181: the browser → Storage → RPC upload shared by CR uploads (CR-147) and
// project documentation. Both land in the same `cr-uploads` bucket and differ
// only in the path prefix (CR id vs project id) and in the RPC that links the
// object. Every string here is data (bucket, options), never UI copy.

import { sanitizeFileName } from "@/features/attachments/attachments.utils";
import { supabase } from "@/lib/supabase";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import {
  CR_UPLOAD_BUCKET,
  buildUploadStoragePath,
  checkCrUploadFile,
  resolveCrUploadMimeType,
} from "../cr-uploads";

/**
 * Upload a file under `{prefixId}/{uuid}-{name}` and link it with `link`.
 * Deliberately NOT a single call: the object has to exist in `storage.objects`
 * before the linking RPC can read its Content-Type and size back out of the
 * bucket, which is what makes the server — not the client — decide whether the
 * type is allowed.
 *
 * The type/size pre-check is a UX affordance only. Storage (bucket
 * `allowed_mime_types` + `file_size_limit`), the INSERT policy (well-formed
 * path under a live CR or project the caller is a member of) and the RPC all
 * re-check server-side.
 *
 * On RPC failure the just-uploaded object is removed best-effort, so a refused
 * upload leaves no orphan bytes behind. The removal is allowed by the uploader
 * DELETE policy precisely because the object never became live.
 */
/**
 * The bucket an upload lands in and its client-side rules. CR-235: the
 * suggestion bucket passes its own; the default is CR-147's `cr-uploads`.
 */
export type UploadBucketRules = {
  bucket: string;
  resolveMimeType: (file: File) => string;
  /** Why the file is refused before any byte is sent, or `null`. */
  check: (file: File) => Error | null;
};

const CR_UPLOADS_RULES: UploadBucketRules = {
  bucket: CR_UPLOAD_BUCKET,
  resolveMimeType: resolveCrUploadMimeType,
  check: checkCrUploadFile,
};

export async function uploadToCrUploadsBucket<T>({
  prefixId,
  file,
  link,
  rules = CR_UPLOADS_RULES,
}: {
  prefixId: string;
  file: File;
  link: (storagePath: string) => PromiseLike<PostgrestSingleResponse<T>>;
  rules?: UploadBucketRules;
}): Promise<T> {
  const refusal = rules.check(file);
  if (refusal) throw refusal;
  const contentType = rules.resolveMimeType(file);

  const storagePath = buildUploadStoragePath(prefixId, sanitizeFileName(file.name));

  const { error: uploadError } = await supabase.storage
    .from(rules.bucket)
    .upload(storagePath, file as unknown as Blob, {
      // The RESOLVED type, not `file.type`: the RPC reads this value back out
      // of storage.objects and matches it against the bucket allow-list, so
      // an empty Content-Type would be refused after the bytes had landed.
      contentType,
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const { data, error } = await link(storagePath);

  if (error) {
    // Best-effort: a failed removal must not hide the RPC error the user needs.
    try {
      await supabase.storage.from(rules.bucket).remove([storagePath]);
    } catch {
      // The orphan stays deletable by its uploader; nothing more to do here.
    }
    throw error;
  }

  return data;
}
